import React, { useState } from 'react';
import type { GoalFormData, GoalFormLabels, GoalPeriod } from '../../shared/types/goals';

interface GoalFormProps {
  initialData: GoalFormData;
  onSubmit: (data: GoalFormData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  labels: GoalFormLabels;
  mode: 'new' | 'edit';
  isSubmitting?: boolean;
  isDeleting?: boolean;
  errors?: Record<string, string[]>;
  // For field set goals
  isFieldSetGoal?: boolean;
  availableFields?: Array<{ id: string; name: string }>;
}

const PERIODS: GoalPeriod[] = ['week', 'month', 'year'];

export function GoalForm({
  initialData,
  onSubmit,
  onCancel,
  onDelete,
  labels: l,
  mode,
  isSubmitting = false,
  isDeleting = false,
  errors = {},
  isFieldSetGoal = false,
  availableFields = [],
}: GoalFormProps) {
  const [targetCount, setTargetCount] = useState(initialData.targetCount);
  const [period, setPeriod] = useState<GoalPeriod>(initialData.period);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>(initialData.fieldIds || []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: GoalFormData = {
      targetCount,
      period,
    };
    if (isFieldSetGoal) {
      data.fieldIds = selectedFieldIds;
    }
    onSubmit(data);
  };

  const handleFieldToggle = (fieldId: string) => {
    setSelectedFieldIds((prev) =>
      prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const getPeriodLabel = (p: GoalPeriod) => {
    switch (p) {
      case 'week':
        return l.perWeek;
      case 'month':
        return l.perMonth;
      case 'year':
        return l.perYear;
    }
  };

  const hasBaseErrors = errors.base && errors.base.length > 0;

  return (
    <form onSubmit={handleSubmit}>
      {hasBaseErrors && (
        <div className="alert alert-danger">
          <ul className="mb-0">
            {errors.base.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-group mb-3">
        <div className="form-floating">
          <input
            type="number"
            className="form-control"
            id="goal_target_count"
            value={targetCount}
            onChange={(e) => setTargetCount(parseInt(e.target.value, 10) || 1)}
            min={1}
            required
            data-testid="goal_target_count"
          />
          <label htmlFor="goal_target_count">{l.targetCount}</label>
        </div>
      </div>

      <div className="form-group mb-3">
        <div className="form-floating">
          <select
            className="form-select"
            id="goal_period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as GoalPeriod)}
            required
            data-testid="goal_period"
          >
            <option value="">— {l.selectPeriod} —</option>
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {getPeriodLabel(p)}
              </option>
            ))}
          </select>
          <label htmlFor="goal_period">{l.period}</label>
        </div>
      </div>

      {isFieldSetGoal && availableFields.length > 0 && (
        <div className="form-group mb-3">
          <label className="form-label fw-bold">{l.selectFields}</label>
          <small className="d-block text-muted mb-2">{l.selectFieldsHelp}</small>
          <div className="border rounded p-2">
            {availableFields.map((field) => (
              <div className="form-check" key={field.id}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`goal_field_${field.id}`}
                  checked={selectedFieldIds.includes(field.id)}
                  onChange={() => handleFieldToggle(field.id)}
                  data-testid={`goal_field_${field.id}`}
                />
                <label className="form-check-label" htmlFor={`goal_field_${field.id}`}>
                  {field.name}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="form-group mb-3">
        <small className="text-muted">{l.trackingHelp}</small>
      </div>

      <div className="d-flex gap-2">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || isDeleting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting || isDeleting}
        >
          {l.cancel}
        </button>
        {mode === 'edit' && onDelete && (
          <button
            type="button"
            className="btn btn-danger ms-auto"
            onClick={onDelete}
            disabled={isSubmitting || isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {l.delete}
              </>
            ) : (
              l.delete
            )}
          </button>
        )}
      </div>
    </form>
  );
}

export default GoalForm;
